import { Box, Slider, Typography } from '@mui/material';
import TimeLineButtonStack from './TImeLineButtonStack';
import moment from 'moment';
import React, { useMemo, useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import { PositionBaseDataType, PositionData, PositionMetaData, PositionWithExpiryData } from '../OptionChain/types';

const TimeLineProgress = ({
  date,
  setDate,
  positions = {},
}: {
  date: string;
  setDate: (predate: string) => void;
  positions?: PositionWithExpiryData;
}) => {
  // Local state for slider value
  const [sliderValue, setSliderValue] = useState(
    moment(date).hours() * 60 + moment(date).minutes()
  );

  // Extract trade marks from positions
  const tradeMarks = useMemo(() => {
    const timeCountMap: Record<number, number> = {};
    Object.values(positions || {}).forEach((positionData: PositionData) => {
      Object.values(positionData).forEach((meta: PositionMetaData) => {
        if (meta && meta.positionCandleWise) {
          Object.values(meta.positionCandleWise).forEach((pos: PositionBaseDataType) => {
            if (pos && pos.entryTime) {
              const m = moment(pos.entryTime);
              const value = m.hours() * 60 + m.minutes();
              timeCountMap[value] = (timeCountMap[value] || 0) + 1;
            }
          });
        }
      });
    });
    return Object.entries(timeCountMap).map(([value, count]) => ({
      value: Number(value),
      label: (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ cursor: 'pointer', pointerEvents: 'auto', mt: '8px' }}
          onClick={e => {
            e.stopPropagation();
            // Calculate the new date string for this mark
            const hours = Math.floor(Number(value) / 60);
            const minutes = Number(value) % 60;
            const newDate = moment(date)
              .hour(hours)
              .minute(minutes)
              .format('YYYY-MM-DD HH:mm');
            setDate(newDate);
          }}
        >
          <ShoppingBasketIcon fontSize="small" color="primary" />
        </Box>
      ),
    }));
  }, [positions, date, setDate]);

  // Update sliderValue if date prop changes externally
  useEffect(() => {
    const newValue = moment(date).hours() * 60 + moment(date).minutes();
    if (sliderValue !== newValue) {
      setSliderValue(newValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Memoized debounced setDate
  const debouncedSetDate = useMemo(
    () => debounce((value: number) => {
      const hours = Math.floor(value / 60);
      const minutes = value % 60;
      const newDate = moment(date)
        .hour(hours)
        .minute(minutes)
        .format('YYYY-MM-DD HH:mm');
      if (newDate !== date) {
        setDate(newDate);
      }
    }, 1000),
    [date, setDate]
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetDate.cancel();
    };
  }, [debouncedSetDate]);

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      width={'100%'}
      border={'1px solid rgb(0 0 0 / 23%)'}
      borderRadius={'0.4em'}
      ml={1}
    >
      <TimeLineButtonStack date={date} setDate={setDate} />
      <Box display={'flex'} mx={1} alignItems={'center'} >
        <Typography variant="body2" flexShrink={0} sx={{ alignItems: 'start'}}>
          9:16
        </Typography>
        <Slider
          sx={{
            flexGrow: 1,
            mx: 2,
            py: 1,
            mb: '20px', // reserve space below for icon above the line
            '& .MuiSlider-markLabel': {
              top: '8px', // below the line
              left: '50%', // horizontal center
              position: 'absolute',
              transform: 'translateX(-50%)', // horizontal centering
              minWidth: '24px',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
              m: 0,
            },
          }}
          size="small"
          valueLabelDisplay="on"
          valueLabelFormat={moment(date).format('HH:mm')}
          min={556}
          max={929}
          step={1}
          value={sliderValue}
          onChange={(_, value) => {
            setSliderValue(value as number);
            debouncedSetDate(value as number);
          }}
          marks={tradeMarks}
        />
        <Typography variant="body2" flexShrink={0} sx={{ alignItems: 'start'}}>
          15:29
        </Typography>
      </Box>
    </Box>
  );
};

export default TimeLineProgress;
